
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table';
import html2pdf from 'html2pdf.js';
import { Pencil, Plus, Download, Search, Trash2 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './farmerdatas.css';
import {Link} from 'react-router-dom';
const DataTable = () => {
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newFarmer, setNewFarmer] = useState({ farmid: '', name: '', area: '', ph: '' });
  const [globalFilter, setGlobalFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFarmerId, setSelectedFarmerId] = useState(null);
  const tableRef = useRef();

  useEffect(() => {
    fetch('https://kmtea.onrender.com/farmers')
      .then(res => res.json())
      .then(setData)
      .catch(console.error);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewFarmer(prev => ({ ...prev, [name]: value }));
  };

  const handleAddFarmer = async (e) => {
    e.preventDefault();
    const url = newFarmer.farmid ? `https://kmtea.onrender.com/farmer/${newFarmer.farmid}` : 'https://kmtea.onrender.com/farmeradd';
    const method = newFarmer.farmid ? 'PUT' : 'POST';

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newFarmer)
    });

    const result = await res.json();

    if (result.success) {
      if (newFarmer.farmid) {
        setData(prev => prev.map(f => f.farmid === newFarmer.farmid ? newFarmer : f));
        toast.success('Farmer updated successfully');
      } else {
        setData(prev => [...prev, result.newFarmer]);
        toast.success('Farmer added successfully');
      }
      setNewFarmer({ farmid: '', name: '', area: '', ph: '' });
      setIsModalOpen(false);
    } else {
      toast.error('Failed to save farmer');
    }
  };

  const columns = useMemo(() => [
    { Header: 'ID', accessor: 'farmid' },
    {
      Header: 'Name',
      accessor: 'name',
     Cell: ({ row }) => (
             <Link
               to={`/dashboard/farmer/${row.original.farmid}`}
               state={row.original}
               className="farmer-link"
             >
               {row.original.name}
             </Link>
           )
    },
    { Header: 'Location', accessor: 'area' },
    { Header: 'Phone Number', accessor: 'ph' },
    {
      Header: 'Actions',
      accessor: 'actions',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="action-icons">
          <span
            title="Edit Farmer"
            onClick={() => {
              setNewFarmer({
                farmid: row.original.farmid,
                name: row.original.name,
                area: row.original.area,
                ph: row.original.ph
              });
              setIsModalOpen(true);
            }}
            style={{ color: '#007bff', cursor: 'pointer', marginRight: '8px' }}
          >
            <Pencil size={18} />
          </span>
          <span
            title="Delete Farmer"
            onClick={() => {
              setSelectedFarmerId(row.original.farmid);
              setShowDeleteModal(true);
            }}
            style={{ color: 'red', cursor: 'pointer' }}
          >
            <Trash2 size={18} />
          </span>
        </div>
      )
    }
  ], []);

  const {
    getTableProps, getTableBodyProps, headerGroups, prepareRow, page,
    pageOptions, gotoPage, nextPage, previousPage, canNextPage, canPreviousPage,
    setGlobalFilter: setFilter, state: { pageIndex }
  } = useTable({
    columns,
    data,
    initialState: { pageSize: 10 }
  }, useGlobalFilter, useSortBy, usePagination);

  const handleSearch = (e) => {
    setGlobalFilter(e.target.value || undefined);
    setFilter(e.target.value || undefined);
  };

  const handleDownloadPDF = () => {
    html2pdf().from(tableRef.current).save('farmers.pdf');
  };

  const handleDeleteFarmer = async () => {
    try {
      const res = await fetch(`https://kmtea.onrender.com/farmer/${selectedFarmerId}`, {
        method: 'DELETE'
      });
      const result = await res.json();
      if (result.success) {
        setData(prev => prev.filter(f => f.farmid !== selectedFarmerId));
        toast.success('Farmer deleted successfully');
      } else {
        toast.error('Failed to delete farmer');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Error deleting farmer');
    }
    setShowDeleteModal(false);
    setSelectedFarmerId(null);
  };

  return (
    <div className="datatable-container">
      <div className="header-actions">
        <h2>Farmers Details</h2>
        <div className="search-filter-wrapper">
          <Search size={16} />
          <input
            type="text"
            placeholder="Quick search..."
            value={globalFilter || ''}
            onChange={handleSearch}
          />
        </div>
        <div className="action-btns">
          <button onClick={handleDownloadPDF}><Download size={16} /> Download</button>
          <button className="add-btn" onClick={() => {
            setNewFarmer({ farmid: '', name: '', area: '', ph: '' });
            setIsModalOpen(true);
          }}><Plus size={16} /> Add new</button>
        </div>
      </div>

      <div ref={tableRef}>
        <table {...getTableProps()} className="styled-table">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={row.id}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>⏮</button>
        <button onClick={previousPage} disabled={!canPreviousPage}>◀</button>
        <span>Page {pageIndex + 1} of {pageOptions.length}</span>
        <button onClick={nextPage} disabled={!canNextPage}>▶</button>
        <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>⏭</button>
      </div>

      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h3>{newFarmer.farmid ? 'Edit Farmer' : '+ Add New Farmer'}</h3>
            <form onSubmit={handleAddFarmer}>
              <div className="form-group">
                <label>Name</label>
                <input type="text" name="name" value={newFarmer.name} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input type="text" name="area" value={newFarmer.area} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="text" name="ph" value={newFarmer.ph} onChange={handleInputChange} required />
              </div>
              <button type="submit">{newFarmer.farmid ? 'Update' : 'Add'}</button>
              <button type="button" onClick={() => setIsModalOpen(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this farmer?</p>
            <div className="modal-buttons">
              <button className="delete-confirm-btn" onClick={handleDeleteFarmer}>Yes, Delete</button>
              <button onClick={() => setShowDeleteModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={2000} hideProgressBar />
    </div>
  );
};

export default DataTable;
